// Copyright (c) 2025 Audere, All Rights Reserved

import { Box, Container, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useRouteError } from "react-router";
import { useSelector } from "react-redux";
import { createStyleSheet } from "../utils/styles";
import AppLogo from "../../assets/HP_Logo.svg";
import { selectCurrentToken } from "../features/auth/authSlice";
import { apiSlice, logOutHelper } from "../features/api/apiSlice";
import { useAppDispatch } from "../features/store";
import { LogoutReasons } from "../types/logging";
import { useCallback } from "react";
import { HttpStatus } from "../types/auth";

const ErrorPage = () => {
  const error = useRouteError() as {
    status?: number;
    errorDescription?: string;
  };

  const { t } = useTranslation("ErrorPage");
  const navigate = useNavigate();
  const isLoggedIn = !!useSelector(selectCurrentToken);
  const dispatch = useAppDispatch();

  const statusCode = error?.status;

  const handleLoginRedirect = useCallback(() => {
    if (
      (statusCode === HttpStatus.UNAUTHORIZED || statusCode === HttpStatus.FORBIDDEN) &&
      isLoggedIn
    ) {
      logOutHelper(
        dispatch,
        apiSlice,
        error.errorDescription === t("unauthorizedRoleMessage")
          ? LogoutReasons.UnauthorizedRole
          : LogoutReasons.ApiError
      );
    }
    navigate("/");
  }, [error, isLoggedIn, dispatch, navigate, statusCode, t]);

  return (
    <Box sx={styles.wrapper}>
      <Container maxWidth="sm" sx={styles.container}>
        <Box component="img" src={AppLogo} alt="HealthPulse AI" />
        <Typography variant="h4" sx={styles.heading}>
          {statusCode === HttpStatus.UNAUTHORIZED || statusCode === HttpStatus.FORBIDDEN
            ? `${statusCode} ${t("notAuthorized")}`
            : t("errorTitle")}
        </Typography>
        <Typography variant="body1" sx={styles.description}>
          {error.errorDescription
            ? error.errorDescription
            : statusCode === HttpStatus.UNAUTHORIZED || statusCode === HttpStatus.FORBIDDEN
              ? t("unauthorizedMessage")
              : t("UnexpectedError")}
          <Box component="span" onClick={handleLoginRedirect} sx={styles.linkText}>
            {statusCode === HttpStatus.UNAUTHORIZED || statusCode === HttpStatus.FORBIDDEN
              ? t("backToLogin")
              : t("backHome")}
          </Box>
        </Typography>
      </Container>
    </Box>
  );
};

const styles = createStyleSheet({
  wrapper: {
    minHeight: "100vh",
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    bgcolor: "#fff",
  },
  container: {
    textAlign: "center",
  },
  heading: {
    fontWeight: "bold",
    mt: 2,
  },
  description: {
    mb: 4,
  },
  linkText: {
    color: theme => theme.palette.primary.main,
    textDecoration: "underline",
    cursor: "pointer",
    fontWeight: 500,
    ml: 1,
  },
});

export default ErrorPage;
